import { Routes, Route, useLocation } from 'react-router-dom';
import { Suspense, lazy, useEffect } from 'react';
import { Layout } from './components/Layout';
import { UIProvider } from './contexts/UIContext';
import { HomePage } from './pages/HomePage';
import { NewsletterPopup } from './components/NewsletterPopup';
import { trackPageView } from './utils/analytics';
import { useScrollToTop } from './hooks/useScrollToTop';

const ComparePage = lazy(() => import('./pages/ComparePage'));
const Reviews = lazy(() => import('./pages/Reviews').then(({ Reviews }) => ({ default: Reviews })));
const BlogPage = lazy(() => import('./pages/BlogPage').then(({ BlogPage }) => ({ default: BlogPage })));
const TestimonialsPage = lazy(() =>
  import('./pages/TestimonialsPage').then(({ TestimonialsPage }) => ({ default: TestimonialsPage })),
);
const AboutPage = lazy(() => import('./pages/AboutPage').then(({ AboutPage }) => ({ default: AboutPage })));
const AffiliateRedirect = lazy(() =>
  import('./components/AffiliateRedirect').then(({ AffiliateRedirect }) => ({ default: AffiliateRedirect })),
);
const LoginPage = lazy(() => import('./pages/LoginPage').then(({ LoginPage }) => ({ default: LoginPage })));
const PasswordResetPage = lazy(() =>
  import('./pages/PasswordResetPage').then(({ PasswordResetPage }) => ({ default: PasswordResetPage })),
);
const PasswordResetConfirmPage = lazy(() =>
  import('./pages/PasswordResetConfirmPage').then(({ PasswordResetConfirmPage }) => ({ default: PasswordResetConfirmPage })),
);
const NewsletterDashboard = lazy(() =>
  import('./pages/NewsletterDashboard').then(({ NewsletterDashboard }) => ({ default: NewsletterDashboard })),
);
const CategoryPage = lazy(() => import('./pages/CategoryPage').then(({ CategoryPage }) => ({ default: CategoryPage })));
const TermsOfUsePage = lazy(() =>
  import('./pages/TermsOfUsePage').then(({ TermsOfUsePage }) => ({ default: TermsOfUsePage })),
);
const PrivacyPolicyPage = lazy(() =>
  import('./pages/PrivacyPolicyPage').then(({ PrivacyPolicyPage }) => ({ default: PrivacyPolicyPage })),
);
const SubscribePage = lazy(() => import('./pages/SubscribePage').then(({ SubscribePage }) => ({ default: SubscribePage })));
const ThankYouPage = lazy(() => import('./pages/ThankYouPage').then(({ ThankYouPage }) => ({ default: ThankYouPage })));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage').then(({ NotFoundPage }) => ({ default: NotFoundPage })));
const ChangelogPage = lazy(() => import('./pages/ChangelogPage').then(({ ChangelogPage }) => ({ default: ChangelogPage })));
const PricingPage = lazy(() => import('./pages/PricingPage').then(({ PricingPage }) => ({ default: PricingPage })));
const LotCalculatorPage = lazy(() => import('./pages/tools/LotCalculatorPage'));
const CalculatorFirstPage = lazy(() => import('./pages/tools/CalculatorFirstPage'));
const BlogPostPage = lazy(() => import('./pages/BlogPostPage'));
const ReviewDetailPage = lazy(() => import('./pages/ReviewDetailPage'));
const BestPropFirmsPage = lazy(() => import('./pages/BestPropFirmsPage'));

const LoadingFallback = () => (
  <div className="min-h-[50vh] bg-gray-950 text-yellow-100 flex items-center justify-center">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-yellow-400 mx-auto mb-4"></div>
      <p className="text-yellow-400">Loading...</p>
    </div>
  </div>
);

export function App() {
  useScrollToTop();
  const location = useLocation();

  useEffect(() => {
    trackPageView(location.pathname + location.search);
  }, [location]);

  return (
    <UIProvider>
      {location.pathname === '/' && <NewsletterPopup />}

      <Suspense fallback={<LoadingFallback />}>
        <Routes>
          <Route path="/" element={<Layout />}>
            <Route index element={<HomePage />} />
            <Route path="compare" element={<ComparePage />} />
            <Route path="reviews" element={<Reviews />} />
            <Route path="reviews/:firmSlug" element={<ReviewDetailPage />} />
            <Route path="blog" element={<BlogPage />} />
            <Route path="blog/:slug" element={<BlogPostPage />} />
            <Route path="testimonials" element={<TestimonialsPage />} />
            <Route path="about" element={<AboutPage />} />
            <Route path="go/:firmId" element={<AffiliateRedirect />} />
            <Route path="login" element={<LoginPage />} />
            <Route path="reset-password" element={<PasswordResetPage />} />
            <Route path="reset-password/confirm" element={<PasswordResetConfirmPage />} />
            <Route path="newsletter-dashboard" element={<NewsletterDashboard />} />
            <Route path="blog/category/:slug" element={<CategoryPage />} />
            <Route path="terms-of-use" element={<TermsOfUsePage />} />
            <Route path="privacy-policy" element={<PrivacyPolicyPage />} />
            <Route path="changelog" element={<ChangelogPage />} />
            <Route path="pricing" element={<PricingPage />} />
            <Route path="subscribe" element={<SubscribePage />} />
            <Route path="thank-you" element={<ThankYouPage />} />
            <Route path="best-prop-firms" element={<BestPropFirmsPage />} />
            <Route path="tools/lot-calculator" element={<CalculatorFirstPage />} />
            <Route path="tools/trader-workspace" element={<LotCalculatorPage />} />
            <Route path="*" element={<NotFoundPage />} />
          </Route>
        </Routes>
      </Suspense>
    </UIProvider>
  );
}

export default App;
